import React, { ReactElement, ReactNode, useState } from 'react';
import ButtonThatOpensPopup from '@app/components/popup/ButtonThatOpensPopup';
import StatusSelector from '@mortee/routes/validationSystem/statusEdit/StatusSelector';
import {
  StatusReason,
  SupplierValidationRecordStatus,
  SupplierValidationRecordStatusObject,
} from '@mortee/domain/validationSystem';
import { FormInputProps } from '@app/utils/form/form';
import { ForwardingFC } from '@app/domain/technicals/components';
import { validationRecordStatusWithArrowSpecialComponents } from '@mortee/domain/validationSystemInfoComponents';
import StatusReasonSelector, {
  compareReasonsWithOtherLast,
} from '@mortee/routes/validationSystem/statusEdit/StatusReasonSelector';
import styled from '@emotion/styled';
import TooltipWithFourCorners from '@app/components/TooltipWithFourCorners';
import SVG from '@app/components/SVG';
import CommentImage from '@app/images/comment.svg';
import { observer } from 'mobx-react';
import { PopupPlacement } from '@app/components/popup/PopupBase';

type Props = FormInputProps<SupplierValidationRecordStatusObject, false> & {
  id: string;
  options?: SupplierValidationRecordStatus[];
  dataTestId?: string;
  disabled: boolean;
  className?: string;
};

enum PopupView {
  selectStatus = 'selectStatus',
  selectReasons = 'selectReasons',
}

// Statuses whose transition requires picking reason(s); ClarificationExternal allows several, the rest exactly one.
function isReasonRequiredStatus(status: SupplierValidationRecordStatus): boolean {
  return (
    status === SupplierValidationRecordStatus.canceled ||
    status === SupplierValidationRecordStatus.clarificationExternal ||
    status === SupplierValidationRecordStatus.topUpRequired
  );
}

function reasonSelectorTexts(status: SupplierValidationRecordStatus): { title: string; explanation: string } {
  if (status === SupplierValidationRecordStatus.clarificationExternal) {
    return { title: 'Clarification - External', explanation: 'Choose one or more clarification reasons:' };
  }

  if (status === SupplierValidationRecordStatus.topUpRequired) {
    return { title: 'Top-Up Required', explanation: 'Choose one or more reasons:' };
  }

  return { title: 'Cancel record', explanation: 'Choose one of the following reasons for cancellation:' };
}

const SelectStatusInput: ForwardingFC<HTMLButtonElement, Props> = observer(
  React.forwardRef(
    ({ id, dataTestId = id, options, value: currentStatus, onChange, accessibilityLabel, disabled, className }, ref) => {
      const [currentPopupView, setCurrentPopupView] = useState<PopupView>(PopupView.selectStatus);
      const [pendingReasonStatus, setPendingReasonStatus] = useState<SupplierValidationRecordStatus | undefined>(undefined);

      function handleSelectStatus(selectedStatus: SupplierValidationRecordStatus, onDone: () => void): void {
        if (isReasonRequiredStatus(selectedStatus)) {
          setPendingReasonStatus(selectedStatus);
          setCurrentPopupView(PopupView.selectReasons);
        } else {
          onChange?.({ value: selectedStatus, reasons: null });
          onDone();
        }
      }

      function handleReasonsSelection(
        status: SupplierValidationRecordStatus,
        selectedReasons: StatusReason[],
        otherReasonText: string | null,
        onDone: () => void,
      ): void {
        onChange?.({ value: status, reasons: selectedReasons, otherReasonText });
        onDone();
      }

      function getPlacement(): PopupPlacement {
        switch (currentPopupView) {
          case PopupView.selectStatus: {
            return 'bottom';
          }
          case PopupView.selectReasons: {
            return 'bottom-left';
          }
        }
      }

      function renderPopupContent(onDone: () => void): ReactElement {
        switch (currentPopupView) {
          case PopupView.selectStatus: {
            return (
              <StatusSelector
                options={options}
                onSelect={(supplierValidationRecordStatusObject): void =>
                  handleSelectStatus(supplierValidationRecordStatusObject, onDone)
                }
              />
            );
          }
          case PopupView.selectReasons: {
            const reasonStatus = pendingReasonStatus ?? SupplierValidationRecordStatus.canceled;
            const { title, explanation } = reasonSelectorTexts(reasonStatus);

            return (
              <StatusReasonSelector
                status={reasonStatus}
                allowMultiple={
                  reasonStatus === SupplierValidationRecordStatus.clarificationExternal ||
                  reasonStatus === SupplierValidationRecordStatus.topUpRequired
                }
                initialValues={currentStatus?.value === reasonStatus ? (currentStatus.reasons ?? []) : []}
                title={title}
                explanation={explanation}
                onSelect={(selectedReasons, otherReasonText): void =>
                  handleReasonsSelection(reasonStatus, selectedReasons, otherReasonText, onDone)
                }
                onNotSelect={(): void => onDone()}
              />
            );
          }
        }
      }

      const sortedReasons = [...(currentStatus?.reasons ?? [])].sort(compareReasonsWithOtherLast);

      return (
        <Row className={className}>
          <ButtonThatOpensPopup
            id={id}
            dataTestId={dataTestId}
            appearance='text'
            cornerType='circle'
            colorScheme='grey'
            shadowType='none'
            popupKey={currentPopupView}
            popupPlacement={getPlacement()}
            aria-label={
              currentStatus
                ? `${validationRecordStatusWithArrowSpecialComponents.text(currentStatus?.value)} - ${accessibilityLabel}`
                : accessibilityLabel
            }
            popupContent={(onDone): ReactNode => renderPopupContent(onDone)}
            onClosed={(): void => {
              setCurrentPopupView(PopupView.selectStatus);
              setPendingReasonStatus(undefined);
            }}
            ref={ref}
            disabled={disabled}
          >
            {validationRecordStatusWithArrowSpecialComponents.render(currentStatus?.value) ?? 'Unknown'}
          </ButtonThatOpensPopup>
          {sortedReasons.length ? (
            <DefaultCursor>
              <ReasonTooltip
                title={
                  <ReasonTooltipContent data-testid='supplier-validation-management-table-cancel-reason-text'>
                    {sortedReasons.length === 1 ? (
                      sortedReasons[0].reason
                    ) : (
                      <ReasonTooltipList>
                        {sortedReasons.map((reason) => (
                          <li key={reason.id}>{reason.reason}</li>
                        ))}
                      </ReasonTooltipList>
                    )}
                  </ReasonTooltipContent>
                }
                // 0 leave-delay flickers (close-open-close); empty transition drops the fade lag.
                mouseLeaveDelay={0.1}
                transitionName=''
                placement='top'
              >
                <SVGThatDoesNotShowFocus
                  image={CommentImage}
                  // Empty label omits the SVG <title> browsers render as a duplicate native tooltip; a11y via aria-label.
                  accessibilityLabel=''
                  role='img'
                  aria-label='Status reasons'
                  // Make it focusable for accessibility
                  tabIndex={0}
                  data-testid='supplier-validation-management-table-cancel-reason-icon'
                />
              </ReasonTooltip>
            </DefaultCursor>
          ) : null}
        </Row>
      );
    },
  ),
);

export default SelectStatusInput;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

// && outranks TooltipUnstyled's .ant-tooltip-inner reset so these styles win.
const ReasonTooltip = styled(TooltipWithFourCorners)`
  && .ant-tooltip-inner {
    background-color: var(--white);
    border: 1px solid var(--gray-solid-200);
    border-radius: 8px;
    box-shadow: var(--box-shadow-3);
    padding: 12px 16px;
  }
`;

const ReasonTooltipContent = styled.div`
  max-width: 320px;
  text-align: start;
  font-size: 13px;
  line-height: 1.4;
`;

// No flex: a flex container blockifies <li> and drops the bullet markers.
const ReasonTooltipList = styled.ul`
  margin: 0;
  padding-inline-start: 18px;
  list-style: disc outside;

  li:not(:last-child) {
    margin-bottom: 6px;
  }
`;

const DefaultCursor = styled.div`
  cursor: default;
`;

const SVGThatDoesNotShowFocus = styled(SVG)`
  &:focus:not(:focus-visible) {
    outline: none;
  }

  --icon-width-padding: 3px;
  --icon-height-padding: 8px;
  --icon-width: 17px;
  --icon-height: 16px;

  width: calc(var(--icon-width) + 2 * var(--icon-width-padding));
  height: calc(var(--icon-height) + 2 * var(--icon-height-padding));

  padding: var(--icon-height-padding) var(--icon-width-padding);
`;
